import api from "@/lib/axios";
import type { ApiResponse } from "@/types";

export interface FinanceSummary {
    period: { from: string; to: string };
    revenue: number;            // net revenue (gross − returns + exchanges)
    discount_total: number;
    cogs: number;
    gross_profit: number;
    gross_margin_pct: number;
    // Breakdown of what nets into `revenue` (added with the reporting engine).
    gross_sales?: number;
    returns_total?: number;
    exchange_sales?: number;
    cash_collected?: number;
    // Realized-profit lens (R2): a SECONDARY view shown beside the accrual
    // `gross_profit`, never replacing or summing into it. Present only when the
    // backend computes it, so an older backend renders nothing.
    realized?: {
        accrual_profit: number;         // = gross_profit (accrual), for reference
        credit_given_profit: number;    // profit granted on credit this period
        realized_from_repayments: number;
        realized_at_checkout: number;   // accrual − credit_given
        realized_profit: number;        // realized_at_checkout + realized_from_repayments
    };
    // Point-in-time snapshot: gross profit still locked in unpaid nasiya (at-risk).
    nasiya_locked_profit?: number;
    // Origin-basis P&L lens (secondary/analytical): returns attributed to the
    // period the sale was MADE, not the refund date. Shown beside the accrual
    // figures above, never replacing them. Present only when the engine emits it.
    origin_pnl?: {
        net_revenue: number;
        cogs: number;
        gross_profit: number;
        returns: number;
        basis: "origin";
    };
    total_expenses: number;
    net_profit: number;
    net_margin_pct: number;
    total_receivables: number;
    receivables_count: number;
    total_payables: number;
    payables_count: number;
    expense_by_category: Array<{
        name: string;
        color: string;
        total: number;
    }>;
}

export interface CashFlowRow {
    date: string;
    revenue: number;   // net revenue (accrual goods value: sales − returns + exchanges)
    cash: number;      // cash actually collected (money in/out)
    expenses: number;
    net: number;       // cash − expenses
}

export interface ExpenseCategory {
    id: string;
    name: string;
    icon: string;
    color: string;
    is_system: boolean;
    /** True = real expense (lowers net profit); false = cash movement (drawer only). */
    affects_profit: boolean;
    expense_count: number;
    total_amount: number;
    created_at: string;
}

export interface Expense {
    id: string;
    category: string | null;
    category_name: string;
    category_color: string;
    category_icon: string;
    /** Read: the category's affects_profit flag (null if uncategorized). */
    category_affects_profit?: boolean | null;
    amount: string;
    description: string;
    store_id: string | null;
    store_name: string;
    /** The cash-drawer shift this rasxod was paid from (POS). */
    shift?: string | null;
    expense_date: string;
    payment_method: string;
    performed_by_id: string;
    performed_by_name: string;
    receipt_image: string;
    notes: string;
    created_at: string;
}

// ─── API Methods ─────────────────────────────────────────────────────────────

export interface ExpenseListResponse {
    results: Expense[];
    count: number;
    total_amount: number;
}

export interface ReceivablesResponse {
    results: any[];
    count: number;
    total_receivables: number;
}

export interface PayablesResponse {
    results: any[];
    count: number;
    total_payables: number;
}

export function getFinanceSummary(params?: {
    date_from?: string;
    date_to?: string;
    store_id?: string;
}) {
    return api.get<ApiResponse<FinanceSummary>>("/finance/summary/", { params });
}

export function getCashFlow(params?: {
    date_from?: string;
    date_to?: string;
    store_id?: string;
}) {
    return api.get<ApiResponse<CashFlowRow[]>>("/finance/cashflow/", { params });
}

export function getExpenseCategories() {
    return api.get<ApiResponse<ExpenseCategory[]>>("/finance/categories/");
}

export function createExpenseCategory(data: Partial<ExpenseCategory>) {
    return api.post<ApiResponse<ExpenseCategory>>("/finance/categories/", data);
}

export function getExpenses(params?: {
    date_from?: string;
    date_to?: string;
    store_id?: string;
    category?: string;
    page?: number;
    page_size?: number;
}) {
    return api.get<ApiResponse<ExpenseListResponse>>("/finance/expenses/", { params });
}

export function createExpense(data: Partial<Expense>) {
    return api.post<ApiResponse<Expense>>("/finance/expenses/", data);
}

export function deleteExpense(id: string) {
    return api.delete<ApiResponse<null>>(`/finance/expenses/${id}/`);
}

export function getReceivables(params?: { search?: string; page?: number; page_size?: number }) {
    return api.get<ApiResponse<ReceivablesResponse>>("/finance/receivables/", { params });
}

export function getPayables(params?: { store_id?: string; supplier_id?: string; page?: number }) {
    return api.get<ApiResponse<PayablesResponse>>("/finance/payables/", { params });
}

// ─── Currency settings (single source of truth) ───────────────────────────────

export interface CurrencySettings {
    base_currency: string;
    display_symbol: string;
    rate_source: "MANUAL" | "CBU";
    auto_fetch_enabled: boolean;
    tracked_currencies: string[];
    last_synced_at: string | null;
    last_sync_source: string;
}

export function getCurrencySettings() {
    return api.get<ApiResponse<CurrencySettings>>("/finance/currency-settings/");
}

export function updateCurrencySettings(data: Partial<CurrencySettings>) {
    return api.patch<ApiResponse<CurrencySettings>>("/finance/currency-settings/", data);
}

/** Pull latest CBU rates now for this tenant's tracked currencies. */
export function syncCurrencyRatesNow() {
    return api.post<ApiResponse<CurrencySettings>>("/finance/currency-settings/sync/", {});
}

/** Set a manual rate (writes to the single source of truth — ExchangeRate). */
export function setManualRate(currency_code: string, rate: number) {
    return api.post<ApiResponse<null>>("/finance/currency-settings/manual-rate/", { currency_code, rate });
}
